import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ExtendedAccount } from "@features/depots/types";
import { InfoChip } from "@components/InfoChips/types";
import WarningIcon from "@icons/myicons/warning-triangle.svg?react";
import CheckCircleIcon from "@icons/myicons/check-circle-2.svg?react";
import EditIcon from "@icons/myicons/edit-2.svg?react";
import ManualPersonIcon from "@icons/myicons/manual-person.svg?react";
import { formatDateDDMMYYYY } from "@helpers";

const useImportTypeChip = (account: ExtendedAccount): InfoChip => {
  const theme = useTheme();
  const { t } = useTranslation();

  const lastSyncDate = formatDateDDMMYYYY(account.lastSyncDate);
  const lastChange = formatDateDDMMYYYY(account.lastChanged);

  if (account.importType === "auto") {
    return {
      icon: <CheckCircleIcon />,
      text: t("depotsAccountsPage.accountChips.automatic"),
      tooltip: t("depotsAccountsPage.accountChips.automaticTooltip", {
        lastSyncDate,
        lastChange,
      }),
      color: theme.palette.success.lighter,
      textColor: theme.palette.success.dark,
    };
  }

  if (account.importType === "manual") {
    return {
      icon: <WarningIcon />,
      text: t("depotsAccountsPage.accountChips.manual"),
      tooltip: t("depotsAccountsPage.accountChips.manualTooltip", {
        lastSyncDate,
        lastChange,
      }),
      color: theme.palette.warning.lighter,
      textColor: theme.palette.text.warning,
    };
  }

  return {
    icon: <ManualPersonIcon />,
    text: t("depotsAccountsPage.accountChips.userGenerated"),
    tooltip: t("depotsAccountsPage.accountChips.userGeneratedTooltip"),
    color: theme.palette.graphVisual["11"].subtleLight,
    textColor: theme.palette.graphVisual["11"].dark,
  };
};

export const useGetAccountChips = (account: ExtendedAccount): InfoChip[] => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isLg = useMediaQuery(theme.breakpoints.down("lg"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const showFullText = !isLg || isMd;

  const { lastChanged } = account;
  const importTypeChip = useImportTypeChip(account);

  const chips: InfoChip[] = [importTypeChip];

  const formattedChanged = formatDateDDMMYYYY(lastChanged);
  if (formattedChanged) {
    const fullMessage = t("depotsAccountsPage.accountChips.lastActivity", {
      date: formattedChanged,
    });

    chips.push({
      icon: <EditIcon />,
      text: showFullText ? fullMessage : formattedChanged,
      color: theme.palette.graphVisual["02"].subtleLight,
      textColor: theme.palette.graphVisual["02"].dark,
    });
  }

  return chips;
};
